import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiClient } from '../api/client';
import { UserTypeahead } from '../components/UserTypeahead';
import { useToast } from '../toast/ToastProvider';
import { PageHeader, PageShell } from '../components/ui';
import { useI18n } from '../i18n/I18nProvider';

type UserDto = { id: string; email: string; firstName?: string | null; lastName?: string | null };
type GroupDto = { id: string; name: string; description?: string | null; users: UserDto[] };

export const GroupDetailPage: React.FC = () => {
  const { communityId, groupId } = useParams();
  const { request } = useApiClient();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showError, showSuccess } = useToast();
  const [group, setGroup] = useState<GroupDto | null>(null);
  const [desc, setDesc] = useState('');
  const [pendingUsers, setPendingUsers] = useState<UserDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);

  // load via community detail and select the group
  useEffect(() => {
    if (!communityId || !groupId) return;
    // Reuse community endpoint and find the group
    request<any>(`/communities/${communityId}`)
      .then((c) => {
        const g = (c.groups as GroupDto[]).find((x) => x.id === groupId) || null;
        setGroup(g);
        setDesc(g?.description || '');
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [communityId, groupId, request]);

  async function addUsers(e: React.FormEvent) {
    e.preventDefault();
    if (!communityId || !groupId) return;
    const ids = pendingUsers.map((u) => u.id);
    try {
      const updated = await request<GroupDto>(`/communities/${communityId}/groups/${groupId}/users`, {
        method: 'POST',
        body: JSON.stringify({ userIds: ids }),
      });
      setGroup(updated);
      setPendingUsers([]);
      showSuccess(t('communities.usersAdded'));
    } catch (err: any) {
      showError(err?.message || 'Failed to add users');
    }
  }

  async function removeUser(user: UserDto) {
    if (!communityId || !groupId || removingUserId) return;
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
    if (!window.confirm(t('communities.removeGroupMemberConfirm', { name }))) return;

    setRemovingUserId(user.id);
    try {
      await request<void>(`/communities/${encodeURIComponent(communityId)}/groups/${encodeURIComponent(groupId)}/users/${encodeURIComponent(user.id)}`, {
        method: 'DELETE',
      });
      setGroup((current) => current ? { ...current, users: current.users.filter((member) => member.id !== user.id) } : current);
      showSuccess(t('communities.groupMemberRemoved'));
    } catch (err: any) {
      showError(err?.message || t('communities.removeGroupMemberError'));
    } finally {
      setRemovingUserId(null);
    }
  }

  async function saveDescription(e: React.FormEvent) {
    e.preventDefault();
    if (!communityId || !groupId) return;
    try {
      const updated = await request<GroupDto>(`/communities/${communityId}/groups/${groupId}/description`, {
        method: 'POST',
        body: JSON.stringify({ description: desc }),
      });
      setGroup(updated);
      showSuccess(t('communities.descriptionSaved'));
    } catch (err: any) {
      showError(err?.message || 'Failed to save description');
    }
  }

  if (loading) return <PageShell><div className="admin-panel p-5 text-sm text-slate-500" role="status">{t('common.loading')}</div></PageShell>;
  if (error || !group) return <PageShell><div className="admin-panel p-5 text-sm text-red-700" role="alert">{error || t('communities.notFound')}</div></PageShell>;

  return (
    <PageShell>
      <PageHeader
        title={group.name}
        description={group.description || t('communities.groupDescriptionPlaceholder')}
        actions={<button className="btn btn-neutral" onClick={() => navigate(`/communities/${communityId}`)}>{t('communities.back')}</button>}
      />
      <div className="grid gap-4 xl:grid-cols-[minmax(280px,.8fr)_minmax(0,1.4fr)]">
        <form onSubmit={saveDescription} className="admin-panel h-fit p-5">
          <div className="admin-section-heading"><div><h2>{t('communities.groupDescription')}</h2><p>{t('communities.groupDescriptionPlaceholder')}</p></div></div>
          <textarea aria-label={t('communities.groupDescription')} className="form-control w-full" rows={5} value={desc} onChange={(event) => setDesc(event.target.value)} placeholder={t('communities.groupDescriptionPlaceholder')} />
          <button className="btn btn-primary mt-3">{t('communities.saveDescription')}</button>
        </form>

        <section className="admin-panel p-5">
          <div className="admin-section-heading"><div><h2>{t('communities.users')}</h2><p>{t('communities.groupCount', { count: group.users?.length ?? 0 })}</p></div><span className="admin-count-pill">{group.users?.length ?? 0}</span></div>
          <div className="admin-community-people admin-community-people-scroll">
            {group.users?.map((user) => (
              <div key={user.id} className="admin-community-person">
                <span className="admin-person-avatar" aria-hidden="true">{(user.firstName || user.email).slice(0, 1).toLocaleUpperCase()}</span>
                <span className="min-w-0 flex-1"><strong>{[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}</strong><small>{user.email}</small></span>
                <button type="button" className="btn btn-danger" disabled={removingUserId !== null}
                  aria-label={t('communities.removeGroupMemberLabel', { name: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email })}
                  onClick={() => removeUser(user)}>
                  {t(removingUserId === user.id ? 'communities.removingGroupMember' : 'communities.removeGroupMember')}
                </button>
              </div>
            ))}
            {!group.users?.length && <p className="admin-community-empty-copy">{t('communities.noUsers')}</p>}
          </div>
          <form onSubmit={addUsers} className="admin-community-add-form">
            <UserTypeahead communityId={communityId!} onSelect={(user) => setPendingUsers((prev) => prev.some((item) => item.id === user.id) ? prev : [...prev, user])} />
            {pendingUsers.length > 0 && <div className="admin-community-pending">{pendingUsers.map((user) => <span key={user.id} className="admin-community-chip">{user.email}<button type="button" aria-label={`Remove ${user.email}`} onClick={() => setPendingUsers((prev) => prev.filter((item) => item.id !== user.id))}>×</button></span>)}</div>}
            <button className="btn btn-primary" disabled={!pendingUsers.length}>{t('communities.addGroupMembers')}</button>
          </form>
        </section>
      </div>
    </PageShell>
  );
};
